// GA debug overlay: a small draggable floating window that mirrors every call
// the analytics wrapper makes (page views, events, user properties) in real
// time, newest first. Visibility is gated on the localStorage enable flag
// (toggled from My Account > Profile > Analytics); position persists across
// reloads. Debug tooling only: it renders nothing unless the flag is on.
import {
  type AnalyticsLogEntry,
  analyticsOverlayEnabled,
  clearAnalyticsLog,
  getAnalyticsLog,
  setAnalyticsOverlayEnabled,
  subscribeAnalytics,
  subscribeAnalyticsOverlayEnabled,
} from "./analytics.js";

const POSITION_STORAGE_KEY = "alaryiq-analytics-overlay-pos";
const OVERLAY_WIDTH = 320;
const OVERLAY_HEIGHT = 260;

type OverlayPosition = { x: number; y: number };

function readStoredPosition(): OverlayPosition | null {
  try {
    const raw = globalThis.localStorage?.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OverlayPosition;
    if (!Number.isFinite(parsed?.x) || !Number.isFinite(parsed?.y)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredPosition(position: OverlayPosition) {
  try {
    globalThis.localStorage?.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch {
    /* ignore */
  }
}

function clampPosition(position: OverlayPosition, win: Window): OverlayPosition {
  const maxX = Math.max(0, win.innerWidth - OVERLAY_WIDTH);
  const maxY = Math.max(0, win.innerHeight - 40);
  return {
    x: Math.min(Math.max(0, position.x), maxX),
    y: Math.min(Math.max(0, position.y), maxY),
  };
}

function entryTimeLabel(ts: number): string {
  const date = new Date(ts);
  const pad = (value: number, width = 2) => String(value).padStart(width, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
}

function entryTag(kind: AnalyticsLogEntry["kind"]): { text: string; className: string } {
  if (kind === "page_view") return { text: "PV", className: "analytics-overlay-tag-pv" };
  if (kind === "user_property") return { text: "UP", className: "analytics-overlay-tag-up" };
  return { text: "EV", className: "analytics-overlay-tag-ev" };
}

function renderEntries(list: HTMLElement, countBadge: HTMLElement, entries: AnalyticsLogEntry[]) {
  countBadge.textContent = String(entries.length);
  list.textContent = "";
  const doc = list.ownerDocument;
  if (!entries.length) {
    const empty = doc.createElement("p");
    empty.className = "analytics-overlay-empty";
    empty.textContent = "No analytics events yet. Interact with the site to see them here.";
    list.append(empty);
    return;
  }
  // Newest first.
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    const row = doc.createElement("div");
    row.className = "analytics-overlay-row";
    const tag = entryTag(entry.kind);
    const head = doc.createElement("div");
    head.className = "analytics-overlay-row-head";
    const time = doc.createElement("span");
    time.className = "analytics-overlay-time";
    time.textContent = entryTimeLabel(entry.ts);
    const badge = doc.createElement("span");
    badge.className = `analytics-overlay-tag ${tag.className}`;
    badge.textContent = tag.text;
    const label = doc.createElement("span");
    label.className = "analytics-overlay-label";
    label.textContent = entry.label;
    head.append(time, badge, label);
    row.append(head);
    const paramPairs = Object.entries(entry.params ?? {});
    if (paramPairs.length) {
      const params = doc.createElement("div");
      params.className = "analytics-overlay-params";
      params.textContent = paramPairs.map(([key, value]) => `${key}=${String(value)}`).join(" ");
      row.append(params);
    }
    list.append(row);
  }
}

export function mountAnalyticsOverlay(doc: Document = globalThis.document) {
  const win = doc?.defaultView ?? globalThis.window;
  if (!doc?.body || !win) return;

  let overlay: HTMLElement | null = null;
  let unsubscribeLog: (() => void) | null = null;
  let renderQueued = false;

  const destroy = () => {
    unsubscribeLog?.();
    unsubscribeLog = null;
    overlay?.remove();
    overlay = null;
  };

  const create = () => {
    if (overlay) return;
    overlay = doc.createElement("section");
    overlay.className = "analytics-overlay";
    overlay.setAttribute("aria-label", "Google Analytics debug overlay");
    overlay.innerHTML = `
      <header class="analytics-overlay-header">
        <span class="analytics-overlay-grip" aria-hidden="true">&#8942;&#8942;</span>
        <strong class="analytics-overlay-title">GA Overlay</strong>
        <span class="analytics-overlay-count" aria-label="Entry count">0</span>
        <button class="analytics-overlay-clear" type="button">clear</button>
        <button class="analytics-overlay-close" type="button" aria-label="Close overlay">&times;</button>
      </header>
      <div class="analytics-overlay-list"></div>
    `;

    const header = overlay.querySelector<HTMLElement>(".analytics-overlay-header");
    const list = overlay.querySelector<HTMLElement>(".analytics-overlay-list");
    const countBadge = overlay.querySelector<HTMLElement>(".analytics-overlay-count");
    if (!header || !list || !countBadge) return;

    overlay.querySelector(".analytics-overlay-clear")?.addEventListener("click", () => {
      clearAnalyticsLog();
    });
    overlay.querySelector(".analytics-overlay-close")?.addEventListener("click", () => {
      setAnalyticsOverlayEnabled(false);
    });

    const applyPosition = (position: OverlayPosition) => {
      const clamped = clampPosition(position, win);
      if (!overlay) return;
      overlay.style.left = `${clamped.x}px`;
      overlay.style.top = `${clamped.y}px`;
      return clamped;
    };
    let position = readStoredPosition()
      ?? { x: Math.max(0, win.innerWidth - OVERLAY_WIDTH - 16), y: Math.max(0, win.innerHeight - OVERLAY_HEIGHT - 16) };
    position = applyPosition(position) ?? position;

    header.addEventListener("mousedown", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("button")) return;
      event.preventDefault();
      const startX = event.clientX - position.x;
      const startY = event.clientY - position.y;
      const onMove = (move: MouseEvent) => {
        position = applyPosition({ x: move.clientX - startX, y: move.clientY - startY }) ?? position;
      };
      const onUp = () => {
        win.removeEventListener("mousemove", onMove);
        win.removeEventListener("mouseup", onUp);
        writeStoredPosition(position);
      };
      win.addEventListener("mousemove", onMove);
      win.addEventListener("mouseup", onUp);
    });
    win.addEventListener("resize", () => {
      if (overlay) position = applyPosition(position) ?? position;
    });

    // Defer the re-render so a track() fired during another handler's
    // synchronous work never re-enters DOM updates mid-dispatch.
    const queueRender = () => {
      if (renderQueued) return;
      renderQueued = true;
      queueMicrotask(() => {
        renderQueued = false;
        if (overlay) renderEntries(list, countBadge, getAnalyticsLog());
      });
    };
    unsubscribeLog = subscribeAnalytics(queueRender);
    renderEntries(list, countBadge, getAnalyticsLog());
    doc.body.append(overlay);
  };

  const sync = (enabled: boolean) => {
    if (enabled) create();
    else destroy();
  };
  subscribeAnalyticsOverlayEnabled(sync);
  sync(analyticsOverlayEnabled());
}
